import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useMyOrganizations } from '../api/multiOrg'
import type { Permission } from '../api/schema'
import { isMemberKind, type MemberKind } from '../lib/member-kinds'

export type { Permission }

/**
 * One asset-scoped grant of the caller, as `/users/me/organizations` carries
 * it (spec 2026-09-04-03). The frontend learns its scope from the API rather
 * than recomputing it from the asset tree.
 */
export interface OrgScope {
    asset_uid: string
    asset_name?: string
    asset_type_code?: string
    role_code: string
    reach: string
    permissions: string[]
    valid_until?: string | null
    /** One-line address, residents and providers only (spec 2026-10-07-03). */
    asset_address?: string | null
}

/**
 * Anything the API returns with a `permissions` array computed for the current
 * caller on THAT row: assets, documents, interventions, issues. Passing one to
 * `useCan` answers "may I do this *here*" instead of "anywhere in the org".
 */
export interface PermissionBearingEntity {
    permissions?: string[] | null
}

export interface PermissionsState {
    /**
     * The caller's effective permission set in the current org: the membership
     * role's bundle plus what their active asset-scoped grants add.
     *
     * It answers "may I do this ANYWHERE in this org" — which is what the
     * sidebar and the route guard need. Where each permission applies is
     * {@link PermissionsState.scopes}, and what applies to one row is that
     * row's own `permissions`.
     */
    permissions: Permission[]
    /**
     * The caller's active asset-scoped grants. Empty for a purely
     * organization-wide member. Which home a member gets is the membership
     * kind's business, not the scopes' ({@link useMemberKind}).
     */
    scopes: OrgScope[]
    /**
     * The MEMBERSHIP role's own bundle — organization-wide, before any grant is
     * unioned in. `permissions` above is the union and cannot answer "does this
     * caller hold X everywhere?"; this can.
     */
    membershipPermissions: Permission[]
    /**
     * The caller's membership kind in this org (spec 2026-10-06-04):
     * `staff`, `resident` or `provider`. Undefined until the membership
     * summary answers.
     */
    kind?: MemberKind
    /** True while `/users/me/organizations` is in flight. */
    isLoading: boolean
    /** The org code these permissions belong to. */
    org: string
    /**
     * True only when the membership summary for this org was actually found.
     * `useCan` fails closed regardless (an unknown membership grants nothing),
     * but the route guard only *ejects* a user when the answer is known: a
     * failed or unauthenticated `/users/me/organizations` must not bounce
     * someone out of a page the backend would have served.
     */
    membershipKnown: boolean
}

const EMPTY: Permission[] = []

const EMPTY_SCOPES: OrgScope[] = []

const PermissionsContext = createContext<PermissionsState>({
    permissions: EMPTY,
    scopes: EMPTY_SCOPES,
    membershipPermissions: EMPTY,
    isLoading: false,
    org: '',
    membershipKnown: false,
})

/**
 * PermissionsProvider exposes the caller's organization-wide permission set,
 * read from the membership summary the app already fetches for the org
 * switcher (`GET /users/me/organizations`). It deliberately issues **no**
 * request of its own — the payload carries `permissions` per membership, so a
 * second round-trip would only add latency and a second source of truth.
 *
 * It fails closed: while the query is in flight, when the caller is not a
 * member of the org in the URL, or when the request failed, the set is empty
 * and every `useCan()` answers `false`. The frontend only hides — the backend
 * is what enforces (spec 2026-09-04-02, "Permissions are not secrets").
 */
export function PermissionsProvider({
    org,
    children,
}: {
    org: string
    children: ReactNode
}) {
    const { data, isError } = useMyOrganizations()

    const value = useMemo<PermissionsState>(() => {
        const membership = data?.data?.find((o) => o.code === org)
        // "Not answered yet" covers more than react-query's `isLoading`: the
        // query is disabled until an access token exists (see useMyOrganizations),
        // and a disabled query is neither loading nor settled. Treating that as
        // loading is what stops the route guard from bouncing a user whose
        // token is still being hydrated from localStorage. A genuine failure
        // (isError) settles as "no permissions" — fail closed.
        const settled = Boolean(data) || isError
        return {
            permissions: (membership?.permissions as Permission[] | undefined) ?? EMPTY,
            scopes: ((membership as { scopes?: OrgScope[] } | undefined)?.scopes) ?? EMPTY_SCOPES,
            membershipPermissions:
                ((membership as { role?: { permissions?: string[] } } | undefined)?.role
                    ?.permissions as Permission[] | undefined) ?? EMPTY,
            isLoading: !settled,
            org,
            kind: isMemberKind(membership?.kind) ? membership.kind : undefined,
            membershipKnown: Boolean(membership),
        }
    }, [data, isError, org])

    return (
        <PermissionsContext.Provider value={value}>
            {children}
        </PermissionsContext.Provider>
    )
}

/** The whole permission set plus its loading state. */
export function usePermissions(): PermissionsState {
    return useContext(PermissionsContext)
}

/**
 * useCan answers "may the current user do this?".
 *
 * With an `entity`, it reads that row's own `permissions` — the backend
 * computed them in-process from the row's scope columns, so a resident sees
 * `assets.read` on their own flat and nothing on the neighbour's. Without one,
 * it answers the organization-wide union, which is what the sidebar and the
 * route guard ask.
 *
 * Lists never need a check: the API already returned only what the caller may
 * see (spec 2026-09-04-03, frontend change 1).
 *
 * `{ exact: true }` turns the widening off — see {@link CanOptions.exact}.
 *
 * Fails closed: unknown org, loading, missing membership, or an entity whose
 * payload carries no `permissions` all return `false`.
 */
export function useCan(
    permission: Permission | undefined,
    entity?: PermissionBearingEntity | null,
    options?: CanOptions
): boolean {
    const { permissions } = useContext(PermissionsContext)
    const holds = options?.exact ? holdsExactPermission : holdsPermission
    if (!permission) return true
    if (entity !== undefined) {
        // An entity was passed but has no permissions array: the payload
        // predates this field, or the row was never stamped. Fall back to the
        // organization-wide answer rather than hiding a control from an admin.
        if (!entity || !entity.permissions) return holds(permissions, permission)
        return holds(entity.permissions, permission)
    }
    return holds(permissions, permission)
}

export interface CanOptions {
    /**
     * Require the permission code VERBATIM — a narrowed variant does not
     * answer for it.
     *
     * The default widening is right for most controls: a resident holding
     * `issues.write_own` may indeed file an issue, so "New issue" should show.
     * It is wrong for a control whose action the narrowed variant structurally
     * cannot perform. `interventions.write_assigned` is the case that made
     * this necessary: a contractor may edit an intervention assigned to them,
     * but "Plan an intervention" creates a row with NO provider yet, which
     * `Assigned` can never match (internal/authz/scope.go, AllowsEntity), so
     * the backend refuses the create with a 404 while the button renders.
     *
     * Use it only there. Reaching for it on a control the narrowed persona CAN
     * use just hides a legitimate action.
     */
    exact?: boolean
}

/**
 * holdsExactPermission is the un-widened test: the code has to be in the set
 * verbatim. See {@link CanOptions.exact} for when a caller wants it.
 */
export function holdsExactPermission(
    granted: readonly string[],
    permission: Permission
): boolean {
    return granted.includes(permission)
}

/**
 * holds mirrors the backend's `satisfies` (internal/authz/authz.go): a NARROWED
 * action answers a check for its base action on the same resource, never the
 * other way round.
 *
 * Without it a resident — whose set is `issues.read_shared`, `issues.read_own`,
 * `issues.write_own` and nothing else — fails every `useCan('issues.read')`, so
 * the sidebar hides the section and the detail page renders empty, while the
 * API happily serves the rows. The frontend hides; it must hide the same things
 * the backend refuses, and no more.
 *
 * The narrowing itself is enforced server-side: `issues.read` here means "may
 * see SOME issues", and which ones is the scope's business.
 */
export function holdsPermission(granted: readonly string[], permission: Permission): boolean {
    if (holdsExactPermission(granted, permission)) return true
    const separator = permission.indexOf('.')
    if (separator < 0) return false
    const prefix = permission.slice(0, separator + 1)
    const action = permission.slice(separator + 1)
    // Only unqualified actions are widened into: holding `issues.read` does NOT
    // mean holding `issues.read_shared`, which is a distinct grant.
    if (action.includes('_')) return false

    return granted.some((code) => code.startsWith(`${prefix}${action}_`))
}

/**
 * useScopes returns the caller's asset-scoped grants.
 */
export function useScopes(): OrgScope[] {
    return useContext(PermissionsContext).scopes
}

/**
 * useMemberKind returns the caller's membership kind in this organization:
 * `staff`, `resident` or `provider` (spec 2026-10-06-04). The org home shows
 * "My places" to residents and providers and the manager home to staff —
 * including a staff member narrowed by grants, such as an agency director, who
 * the old "grants but no organization-wide `assets.read`" guess sent to "My
 * places". Undefined while the membership summary loads.
 */
export function useMemberKind(): MemberKind | undefined {
    return useContext(PermissionsContext).kind
}

/**
 * useCanOrgWide answers "does the caller hold this organization-wide?": through
 * the membership role, never through an asset-scoped grant. It mirrors the
 * backend's CanOrgWide (internal/authz/authz.go), for the controls of a route
 * gated with RequireOrgWidePermission, which refuses a grant-held permission.
 */
export function useCanOrgWide(permission: Permission): boolean {
    const { membershipPermissions } = useContext(PermissionsContext)
    return holdsPermission(membershipPermissions, permission)
}

/**
 * useCanAll is the multi-permission form — every permission must be granted.
 * Handy for a control that both reads a list and writes to it.
 */
export function useCanAll(required: Permission[]): boolean {
    const { permissions } = useContext(PermissionsContext)
    return required.every((p) => holdsPermission(permissions, p))
}

/**
 * `<Can perm="assets.write">` — the JSX form of `useCan`. Renders `fallback`
 * (nothing by default) when the permission is missing.
 *
 * Use it to hide *actions*. An inline property editor must NOT disappear when
 * the user lacks `write` — it renders read-only instead, so the data stays
 * visible (spec 2026-09-04-02, "Gating").
 */
export function Can({
    perm,
    entity,
    exact,
    fallback = null,
    children,
}: {
    perm: Permission
    /** When given, the check runs against this row's own `permissions`. */
    entity?: PermissionBearingEntity | null
    /** See {@link CanOptions.exact} — off by default. */
    exact?: boolean
    fallback?: ReactNode
    children: ReactNode
}) {
    const allowed = useCan(perm, entity, { exact })
    return <>{allowed ? children : fallback}</>
}

/**
 * A narrowed action reduces to its base action (spec 2026-09-04-03, decisions
 * 7-8): `<resource>.read_shared` / `read_own` / `read_assigned` are covered by
 * `<resource>.read`, and `write_own` / `write_assigned` by `<resource>.write`.
 * Mirrors `authz.BaseAction` in `internal/authz/catalogue.go` — keep both in
 * sync if a narrowed action is ever added.
 */
function baseAction(action: string): string {
    if (
        action === 'read_shared' ||
        action === 'read_own' ||
        action === 'read_assigned'
    ) {
        return 'read'
    }
    if (action === 'write_own' || action === 'write_assigned') {
        return 'write'
    }
    return action
}

/**
 * roleIsAssignable reports whether `rolePermissions` is a subset of the
 * caller's own set. Mirrors the backend's no-escalation rule (`authz.IsSubset`
 * / 403 `permission_escalation`) so the UI can disable the option instead of
 * letting the user discover the refusal after submitting — including the
 * narrowed-action rule: a caller holding `documents.read` may assign a role
 * that only reads `documents.read_shared`. Without it, no org-wide admin
 * could ever be offered a scoped-only role (resident, property_owner,
 * contractor) in the UI, because none of those qualified codes appear in any
 * organization-wide bundle verbatim.
 */
export function isSubsetOf(
    rolePermissions: readonly string[],
    callerPermissions: readonly string[]
): boolean {
    const have = new Set(callerPermissions)
    return rolePermissions.every((p) => {
        if (have.has(p)) return true
        const dot = p.indexOf('.')
        if (dot === -1) return false
        const resource = p.slice(0, dot)
        const action = p.slice(dot + 1)
        const base = baseAction(action)
        return base !== action && have.has(`${resource}.${base}`)
    })
}

/** Hook form of {@link isSubsetOf} against the current caller. */
export function useCanAssignRole(): (rolePermissions: readonly string[]) => boolean {
    const { permissions } = useContext(PermissionsContext)
    return useMemo(
        () => (rolePermissions: readonly string[]) =>
            isSubsetOf(rolePermissions, permissions),
        [permissions]
    )
}
